import { bizGet, bizPatch, bizPost } from '@/shared/api/bizClient';

import type {
  GroomingIntakeInput,
  GroomingFieldTestFeedbackInput,
  GroomingMediaKind,
  GroomingMediaUploadInput,
  GroomingMediaUploadTicket,
  GroomingReportCard,
  GroomingReportInput,
  GroomingSessionDetail,
  GroomingSessionStatus,
  GroomingSessionSummary,
  GroomingWorkflowStepStatus,
} from './types';

interface ListEnvelope<T> {
  items: T[];
}

export async function listGroomingSessions(
  branchId: string,
): Promise<GroomingSessionSummary[]> {
  const response = await bizGet<ListEnvelope<GroomingSessionSummary>>(
    '/biz/grooming/sessions',
    { params: { branchId } },
  );
  return response.items;
}

export async function startGroomingIntake(
  bookingId: string,
  input: GroomingIntakeInput,
): Promise<GroomingSessionDetail> {
  return bizPost<GroomingSessionDetail, GroomingIntakeInput>(
    `/biz/grooming/bookings/${bookingId}/session`,
    input,
  );
}

export async function updateGroomingStatus(
  sessionId: string,
  status: GroomingSessionStatus,
): Promise<GroomingSessionDetail> {
  return bizPatch<GroomingSessionDetail, { status: GroomingSessionStatus }>(
    `/biz/grooming/sessions/${sessionId}/status`,
    { status },
  );
}

export async function upsertGroomingReportCard(
  sessionId: string,
  input: GroomingReportInput,
): Promise<GroomingReportCard> {
  return bizPost<GroomingReportCard, GroomingReportInput>(
    `/biz/grooming/sessions/${sessionId}/report-card`,
    input,
  );
}

export async function updateGroomingWorkflowStep(
  sessionId: string,
  stepKey: string,
  status: GroomingWorkflowStepStatus,
): Promise<GroomingSessionDetail> {
  return bizPatch<GroomingSessionDetail, {
    stepKey: string;
    status: GroomingWorkflowStepStatus;
  }>(
    `/biz/grooming/sessions/${sessionId}/workflow`,
    { stepKey, status },
  );
}

export async function addGroomingHandoffNote(
  sessionId: string,
  note: string,
): Promise<GroomingSessionDetail> {
  return bizPost<GroomingSessionDetail, { note: string }>(
    `/biz/grooming/sessions/${sessionId}/handoff-note`,
    { note },
  );
}

export async function addGroomingFieldTestFeedback(
  sessionId: string,
  input: GroomingFieldTestFeedbackInput,
): Promise<GroomingSessionDetail> {
  return bizPost<GroomingSessionDetail, GroomingFieldTestFeedbackInput>(
    `/biz/grooming/sessions/${sessionId}/field-test-feedback`,
    input,
  );
}

export async function createGroomingMediaUploadTicket(
  sessionId: string,
  input: GroomingMediaUploadInput,
): Promise<GroomingMediaUploadTicket> {
  return bizPost<GroomingMediaUploadTicket, GroomingMediaUploadInput>(
    `/biz/grooming/sessions/${sessionId}/media-upload-url`,
    input,
  );
}

export async function commitGroomingMediaUpload(
  sessionId: string,
  kind: GroomingMediaKind,
  objectKey: string,
): Promise<GroomingReportCard> {
  return bizPost<GroomingReportCard, { kind: GroomingMediaKind; objectKey: string }>(
    `/biz/grooming/sessions/${sessionId}/media`,
    { kind, objectKey },
  );
}
